<template>
  <section
    v-if="visible"
    class="client-cabinet-block client-cabinet-section-card"
    aria-labelledby="client-page-card-title"
    data-testid="client-page-card-block"
    data-field="company.payment_methods.tinkoff client.tinkoff_card_bound tinkoff_payments[].purpose tinkoff_payments[].outcome company.card_consent_links.tariffs company.card_consent_links.agreement company.card_consent_links.privacy"
  >
    <div class="client-cabinet-section-heading">
      <div>
        <h2 id="client-page-card-title">{{ $t("client_page.cabinet.card.title") }}</h2>
        <p>{{ $t("client_page.cabinet.card.subtitle") }}</p>
      </div>
    </div>

    <!-- Привязанная карта видна, пока она есть у клиента, даже если компания выключила Т-Банк: отвязать её клиент вправе всегда (FT-193 REQ-11). -->
    <div v-if="tinkoffCardBound" class="client-cabinet-linked-card" data-testid="client-page-bound-card">
      <span data-field="client.tinkoff_card_number">
        <v-icon small left aria-hidden="true">mdi-credit-card-outline</v-icon>{{ $t("client_page.cabinet.card.bound", { card: unbindCardLabel }) }}
      </span>
      <v-btn text class="client-cabinet-error" min-height="44" :loading="isLoading" :disabled="isLoading" data-testid="client-page-card-remove" @click="unbindCard()">
        {{ $t("tinkoff.unbind") }}
      </v-btn>
    </div>

    <div v-else-if="bindingPending" class="client-cabinet-checking-row" data-testid="client-page-card-checking">
      <div>
        <strong>{{ $t("client_page.cabinet.card.checking") }}</strong>
        <span>{{ $t("client_page.cabinet.card.checking_hint") }}</span>
      </div>
      <span class="client-cabinet-pending-dot" aria-hidden="true"></span>
    </div>

    <template v-else-if="canBind">
      <p class="client-cabinet-card-benefit" data-testid="client-page-card-not-bound">{{ $t("client_page.cabinet.card.not_bound") }}</p>
      <v-btn color="primary" outlined min-height="44" class="client-cabinet-section-action" data-testid="client-page-add-card" @click="showConsent = true">
        <v-icon left aria-hidden="true">mdi-credit-card-plus-outline</v-icon>
        {{ $t("client_page.cabinet.card.add") }}
      </v-btn>
    </template>

    <div v-else class="client-cabinet-card-benefit" data-testid="client-page-card-unavailable">
      <strong>{{ $t("client_page.cabinet.card.unavailable") }}</strong>
      <p>{{ $t("client_page.cabinet.card.unavailable_hint") }}</p>
    </div>

    <!-- REQ-13: Esc и подложка закрывают, как «Закрыть»; пока идёт запрос привязки — нет. -->
    <v-dialog v-model="showConsent" :persistent="isLoading" content-class="client-cabinet-dialog" max-width="640px">
      <v-card>
        <v-card-title>
          <h2 class="text-h6">{{ $t("tinkoff.adding_card") }}</h2>
        </v-card-title>
        <v-card-text>
          <!-- FT-193: тарифы, соглашение и политика проката — только заданные ссылки. -->
          <p v-if="consentLinks.length" class="text-center" data-testid="client-page-consent-links">
            <template v-for="(link, index) in consentLinks">
              <span v-if="index > 0" :key="`sep-${link.key}`" aria-hidden="true"> | </span>
              <a :key="link.key" :href="link.href" target="_blank" rel="noopener" class="client-cabinet-link">{{ $t(link.label) }}</a>
            </template>
          </p>
          <p>{{ $t("tinkoff.payment_for_text") }} {{ $t("tinkoff.consent.unbind_info") }}</p>
          <v-checkbox v-model="acceptTerms" hide-details class="client-cabinet-check" data-testid="client-page-consent-terms"
            :label="$t('tinkoff.consent.client_terms')"></v-checkbox>
          <v-checkbox v-model="acceptAutopay" hide-details class="client-cabinet-check" data-testid="client-page-consent-autopay"
            :label="$t('tinkoff.consent.client_autopay')"></v-checkbox>
        </v-card-text>
        <v-card-actions class="flex-wrap">
          <v-btn text min-height="44" @click="closeConsent">{{ $t("custom.close") }}</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" depressed min-height="44" :loading="isLoading" :disabled="!acceptTerms || !acceptAutopay || isLoading"
            data-testid="client-page-add-card-submit" @click="addCardCreate()">{{ $t("client_page.cabinet.card.add") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </section>
</template>

<script>
// FT-200 STEP-05 (REQ-08, REQ-11): карта для автосписаний Т-Банка в кабинете клиента; FT-193 REQ-11/REQ-12 — привязка и
// отвязка клиентом.
//
// Состояния: «не привязана — сейчас ничего не спишем» / «банк проверяет карту» (свежая привязка без исхода) /
// «•••• 1234 привязана · отвязать». Блок есть, когда у компании работает терминал (payment_methods.tinkoff) или у
// клиента осталась карта.
// Привязка уходит с return_origin — origin кабинета, куда банк вернёт клиента (clientCabinetReturn.base); до перехода
// к банку во вкладку пишется операция привязки (id платежа и карта до привязки) — по ней страница возврата узнает итог.
// Вход — пара номер + фамилия; Authorization вызов сам не ставит.
import clientCabinetReturn from "@/utils/clientCabinetReturn";
import clientCabinetSession from "@/utils/clientCabinetSession";
import { cardBindingPending, reportServerError, NOTIFY_COLORS } from "@/utils/clientCabinetView";
import { isTransientNetworkError } from "@/utils/networkErrors";
import { isTinkoffCardBound, openPaymentUrl } from "@/utils/tinkoffCard";
import { extractValidationDetail } from "@/mixins/setErrorMixin";
import { depositHoldUnbindText } from "@/utils/depositHold";

export const CARDS_PATH = "/api/v1/integrations/public/tinkoff/cards";

const CONSENT_LINKS = [
  { key: "tariffs", label: "tinkoff.tarrifs_link" },
  { key: "agreement", label: "tinkoff.agreement_link" },
  { key: "privacy", label: "tinkoff.privacy_link" },
];

// FT-193: коды отказа публичной привязки/отвязки карты → клиентский текст (setError показал бы для 502 сырой код статуса).
const PUBLIC_CARD_CODE_TEXT = {
  tinkoff_receipt_contact_missing: "tinkoff.errors.receipt_contact_missing_client",
  tinkoff_unreachable: "errors.codes.tinkoff_unreachable",
};

export default {
  name: "CardBlock",
  props: {
    client: { type: Object, default: null },
    company: { type: Object, default: null },
    tinkoffPayments: { type: Array, default: () => [] },
    bookingId: { type: [String, Number], required: true },
    lastName: { type: String, required: true },
    activeDepositHold: { type: Object, default: null },
  },
  data() {
    return {
      showConsent: false,
      acceptTerms: false,
      acceptAutopay: false,
      isLoading: false,
    };
  },
  computed: {
    // Признак карты — tinkoff_card_bound ответа; маска сама по себе карты не доказывает.
    tinkoffCardBound() {
      return isTinkoffCardBound(this.client);
    },
    canBind() {
      const methods = this.company && this.company.payment_methods;
      return !!(methods && methods.tinkoff);
    },
    bindingPending() {
      return cardBindingPending(this.tinkoffPayments);
    },
    visible() {
      return !!this.client;
    },
    // Ссылки согласия (company.card_consent_links): пустые не показываются.
    consentLinks() {
      const links = (this.company && this.company.card_consent_links) || {};
      return CONSENT_LINKS.filter((link) => links[link.key]).map((link) => ({ ...link, href: links[link.key] }));
    },
    // Подпись карты: «•••• 0777»; без цифр в маске — маска как есть.
    unbindCardLabel() {
      const mask = String((this.client && this.client.tinkoff_card_number) || "");
      const digits = mask.replace(/\D/g, "");
      return digits.length >= 4 ? `•••• ${digits.slice(-4)}` : mask;
    },
  },
  watch: {
    // Закрыт Esc или подложкой — флажки согласия снимаются, как у «Закрыть».
    showConsent(open) {
      if (!open) this.closeConsent();
    },
  },
  methods: {
    closeConsent() {
      this.showConsent = false;
      this.acceptTerms = false;
      this.acceptAutopay = false;
    },
    // FT-193 REQ-12: без обоих согласий запрос не уходит.
    addCardCreate() {
      if (!(this.acceptTerms && this.acceptAutopay)) {
        this.$notify(this.$t("tinkoff.consent.required"), "", NOTIFY_COLORS.warning, 4000);
        return Promise.resolve();
      }
      this.isLoading = true;
      return this.axios
        .post(CARDS_PATH, {
          booking_id: this.bookingId,
          last_name: this.lastName,
          consent: true,
          return_origin: clientCabinetReturn.base(this.company),
        })
        .then((response) => {
          const data = (response && response.data) || {};
          if (!data.payment_url) {
            this.$notify(this.$t("errors.error"), this.$t("tinkoff.errors.bind_failed"), NOTIFY_COLORS.error, 5000);
            return;
          }
          clientCabinetSession.startOperation({
            flow: "card",
            paymentId: data.payment_id,
            cardBefore: (this.client && this.client.tinkoff_card_number) || null,
          });
          this.closeConsent();
          // Натив — @capacitor/browser, веб — новая вкладка.
          openPaymentUrl(data.payment_url);
        })
        .catch((error) => {
          this.handlePublicCardError(error, { notFoundKey: "tinkoff.errors.wrong_last_name", failKey: "tinkoff.errors.bind_failed" });
        })
        .then(() => {
          this.isLoading = false;
        });
    },
    // FT-193 REQ-11: клиент отвязывает карту сам.
    unbindCard() {
      return this.$confirm({
        title: this.$t("tinkoff.unbind_confirm_title", { card: this.unbindCardLabel }),
        text: depositHoldUnbindText(this, this.activeDepositHold, "tinkoff.unbind_confirm_text"),
        icon: "mdi-alert-circle-outline",
        confirmButtonText: this.$t("tinkoff.unbind"),
        confirmButtonColor: NOTIFY_COLORS.error,
        cancelButtonText: this.$t("buttons.cancel"),
      }).then((confirmed) => {
        if (!confirmed) return undefined;
        this.isLoading = true;
        return this.axios
          .delete(CARDS_PATH, { data: { booking_id: this.bookingId, last_name: this.lastName } })
          .then(() => {
            this.markCardUnbound();
            this.$notify(this.$t("tinkoff.card_unbound"), "", NOTIFY_COLORS.success, 4000);
          })
          .catch((error) => {
            const code = this.handlePublicCardError(error, { notFoundKey: "tinkoff.errors.unbind_not_found", failKey: "tinkoff.errors.unbind_failed" });
            // Сервер сказал, что карты нет (отвязана сотрудником или в другой вкладке): страница показывала устаревшее.
            if (code === "tinkoff_card_not_bound") this.markCardUnbound();
          })
          .then(() => {
            this.isLoading = false;
          });
      });
    },
    // Карты больше нет: кабинет (владелец client) скрывает блок карты и перечитывает бронирование. Prop не мутируется.
    markCardUnbound() {
      this.$emit("changed", { cardBound: false });
    },
    // Отказ публичной привязки/отвязки — текстом по коду или статусу. setError здесь не годится: он печатает ошибку
    // axios в консоль, а в её config.data — пара номер + фамилия. 404 — неверная пара; коды из PUBLIC_CARD_CODE_TEXT и
    // известные errors.codes — их текст; 429 — лимит; обрыв связи — «нет связи»; прочее — «повторите позже»,
    // 5xx — ещё и в отчёт об ошибках без тела запроса. Возвращает код отказа сервера.
    handlePublicCardError(error, { notFoundKey, failKey }) {
      const status = error && error.response && error.response.status;
      const { code } = extractValidationDetail(error);
      if (status === 404) {
        this.$notify(this.$t("errors.error"), this.$t(notFoundKey), NOTIFY_COLORS.warning, 6000);
        return code;
      }
      this.$notify(this.$t(failKey), this.$t(this.cardErrorKey(error, status, code)), NOTIFY_COLORS.warning, 8000);
      reportServerError(this, `ClientCabinet: карта — ${failKey}`, error);
      return code;
    },
    cardErrorKey(error, status, code) {
      if (PUBLIC_CARD_CODE_TEXT[code]) return PUBLIC_CARD_CODE_TEXT[code];
      if (code && this.$te(`errors.codes.${code}`)) return `errors.codes.${code}`;
      if (status === 429) return "client_page.cabinet.pay_dialog.errors.rate_limited";
      if (!status && isTransientNetworkError(error)) return "client_page.cabinet.pay_dialog.errors.network";
      return "client_page.cabinet.card.retry_later";
    },
  },
};
</script>
